import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  corroborationClause,
  eventAnalysesFor,
  factDating,
  joinClauses,
  runStateLabel,
  type KnowledgePanel,
  type SubjectStatus,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { qk } from '../../api/keys';
import { useEventAttendees } from '../../api/event-hooks';
import { useKnowledgePanel } from '../../api/hooks';
import { panelFactLine } from '../../components/knowledge/knowledge-panel';
import { RunBlock } from '../../components/run-strip/run-block';
import { Prose } from '../../components/ui/prose';
import { StatusDot } from '../../components/ui/status-word';
import { IfWrites } from '../../components/write-access';
import {
  EVENT_ABOUT_HEADING,
  EVENT_ASIDE_ATTENDEES,
  EVENT_ASIDE_NOT_RESEARCHED,
  EVENT_ASIDE_NO_LIST,
  EVENT_ASIDE_ON_RECORD,
  EVENT_ASIDE_RESEARCH,
  EVENT_NO_RESEARCH,
  EVENT_SUMMARY_HEADING,
  EVENT_PROFILE_FROM_PRESS,
  EVENT_PROFILE_HEADING,
  EVENT_NEXT_ADD_ATTENDEES,
  EVENT_NEXT_ANALYSES,
  eventAttendeesOnRecord,
  eventFactsDisclosure,
  eventProfileRows,
  eventSummaryWritten,
  subjectStatusLabel,
  RUN_UNDELIVERED_HEAD,
  undeliveredLine,
} from '../../lib/copy';
import { formatDay } from '../../lib/format';
import { useEventRecord } from './record';
import { useWholeList } from './analysis-factor';
import { statusesByTone, trackedAttendees } from './statuses';
import { EmptyLine } from '../../components/ui/empty-line';

/**
 * **The event's Overview** (the blend spec's record body): the research
 * summary first, with what the record holds about the event behind one
 * disclosure, then what the office wrote about it in office ink beside the
 * research. The aside carries what the event says it is, where research and
 * the attendee list stand, and the people at it already on record, each with
 * their status. Starting research is the top bar's.
 */
/**
 * Whether an attendee scan is going, from the attendee read the aside already
 * polls (same query, no extra request). The scan's own run-state invalidation
 * lives on the Attendees and Analysis tabs, so the Overview reads the knowledge
 * base again itself when the scan ends.
 */
function useScanActive(entityId: string): boolean {
  const queryClient = useQueryClient();
  const org = useOrg();
  const list = useEventAttendees(entityId, {});
  const active = Boolean(list.data?.pages[0]?.scan?.active_run_id);
  const was = useRef(active);
  useEffect(() => {
    if (was.current && !active) {
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
    }
    was.current = active;
  }, [active, queryClient, org.id]);
  return active;
}

export function EventOverviewTab() {
  const { entityId, record, research } = useEventRecord();
  const { summary, statements } = record;
  const scanning = useScanActive(entityId);
  /* Re-read on the minute only while research or an attendee scan is going; the
     research run's state invalidation, and the scan ending below, refresh it
     when the work lands (`useNetwork`'s `poll`). */
  const panel = useKnowledgePanel(entityId, record.subject.account?.id, {
    poll: research.active || scanning,
  });

  return (
    <div className="event-body">
      <div className="event-main">
        {research.active && research.runQuery.data ? (
          <IfWrites>
            <RunBlock
              subjectId={entityId}
              subjectName={record.subject.display_name}
              run={research.runQuery.data}
              pendingDecisions={[]}
              pollFailures={research.runQuery.failureCount}
            />
          </IfWrites>
        ) : null}
        {research.failure ? <div className="src run-failed">{research.failure}</div> : null}

        <section className="event-panel">
          <div className="event-panel-head">
            <h2>{EVENT_SUMMARY_HEADING}</h2>
            {summary ? (
              <p className="event-meta">{eventSummaryWritten(formatDay(summary.updated_at))}</p>
            ) : null}
          </div>
          {summary ? (
            <>
              <div className="event-reading">
                <Prose text={summary.text} />
              </div>
              <NextStep entityId={entityId} />
            </>
          ) : (
            <EmptyLine>{EVENT_NO_RESEARCH}</EmptyLine>
          )}
          {panel.data && panel.data.facts.length > 0 ? <EventFacts panel={panel.data} /> : null}
        </section>

        {statements.length > 0 ? (
          <section className="event-statement">
            <h3>{EVENT_ABOUT_HEADING}</h3>
            {statements.map((statement) => (
              <div key={statement.id}>
                <div className="office">
                  <Prose text={statement.prose} />
                </div>
                <p className="event-meta num">{formatDay(statement.created_at)}</p>
              </div>
            ))}
          </section>
        ) : null}
      </div>

      <aside className="event-aside">
        {record.profile ? (
          <div className="event-aside-block">
            <h4 className="sc">{EVENT_PROFILE_HEADING}</h4>
            <div className="event-aside-about">
              <Prose text={record.profile.description} />
            </div>
            <dl className="event-props">
              {eventProfileRows(record.profile).map((row) => (
                <div key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>
                    {row.value}
                    {row.fromPress ? (
                      <span className="quiet"> · {EVENT_PROFILE_FROM_PRESS}</span>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
        <div className="event-aside-block">
          <dl className="event-props">
            <div>
              <dt>{EVENT_ASIDE_RESEARCH}</dt>
              <dd>
                {research.latestEmpty
                  ? `${RUN_UNDELIVERED_HEAD} · ${undeliveredLine('event_research')}`
                  : research.latestState
                    ? runStateLabel(research.latestState)
                    : EVENT_ASIDE_NOT_RESEARCHED}
                {research.lastCompleteAt ? (
                  <span className="quiet num">{formatDay(research.lastCompleteAt)}</span>
                ) : null}
              </dd>
            </div>
            <AttendeeCount entityId={entityId} />
          </dl>
        </div>
        <OnRecord entityId={entityId} />
      </aside>
    </div>
  );
}

/** The aside's attendee line: how many, and how many of them are already on record. */
function AttendeeCount({ entityId }: { entityId: string }) {
  const list = useEventAttendees(entityId, {});
  const counts = list.data?.pages[0]?.counts;
  if (!counts) return null;
  return (
    <div>
      <dt>{EVENT_ASIDE_ATTENDEES}</dt>
      <dd>
        {counts.total === 0 ? (
          EVENT_ASIDE_NO_LIST
        ) : (
          <>
            <span className="num">{counts.total}</span>
            {counts.linked > 0 ? (
              <span className="quiet">{eventAttendeesOnRecord(counts.linked)}</span>
            ) : null}
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * **The people at the event already on record**, each with their status: the
 * tracked people the attendee list leads to, in the vertical's status order.
 */
export function OnRecord({ entityId }: { entityId: string }) {
  const vertical = useOrg().vertical;
  const whole = useWholeList(entityId, true);
  const order = statusesByTone(vertical);
  const rank = (status: SubjectStatus | null) =>
    status === null ? order.length : order.indexOf(status);
  const people = trackedAttendees(whole.rows).sort(
    (a, b) => rank(a.status) - rank(b.status) || a.name.localeCompare(b.name),
  );
  if (people.length === 0) return null;
  return (
    <div className="event-aside-block">
      <h4 className="sc">{EVENT_ASIDE_ON_RECORD}</h4>
      <ul className="event-ties">
        {people.map((person) => (
          <li key={person.entityId}>
            {person.status ? <StatusDot vertical={vertical} status={person.status} /> : null}
            <Link className="person" to={`/people/${person.entityId}`}>
              {person.name}
            </Link>
            <span className="event-tie-status">
              {person.status ? (subjectStatusLabel(vertical, person.status) ?? '') : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** What the record holds about the event, behind one closed disclosure. */
function EventFacts({ panel }: { panel: KnowledgePanel }) {
  return (
    <details className="event-behind">
      <summary>{eventFactsDisclosure(panel.facts.length)}</summary>
      <ul className="event-fact-rows">
        {panel.facts.map((fact) => {
          const source = joinClauses([factDating(fact), corroborationClause(fact)]);
          return (
            <li key={fact.lineage_id}>
              <span className="event-fact-line">{panelFactLine(fact, panel.entry)}</span>
              {source ? <span className="event-fact-src">{source}</span> : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/**
 * **The event flow's next step**, one quiet line under the research summary
 * (docs/notes/2026-09-24-event-analysis-flow-spec.md): add the attendee list,
 * then the analyses. Only a pointer, read off the list the Attendees tab
 * already reads; nothing while that read is loading or failed.
 */
export function NextStep({ entityId }: { entityId: string }) {
  const vertical = useOrg().vertical;
  const list = useEventAttendees(entityId, {});
  const total = list.data?.pages[0]?.counts.total;
  if (total === undefined) return null;
  if (total > 0 && eventAnalysesFor(vertical).length === 0) return null;
  const [to, label] =
    total === 0
      ? [`/events/${entityId}/attendees`, EVENT_NEXT_ADD_ATTENDEES]
      : [`/events/${entityId}/analysis`, EVENT_NEXT_ANALYSES];
  return (
    <p className="event-next">
      <Link className="link" to={to}>
        {label}
      </Link>
    </p>
  );
}
